import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { COMPANY_PROFILE_CARD_DATA_TOKEN } from '@galaxy/crm/static';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, ReplaySubject, startWith } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { CreateSnapshotRequest, CurrentSnapshot, SnapshotService, SnapshotUrl } from '../../snapshot/snapshot.service';
import { CompanyAccountService } from '../../snapshot/company-account.service';
import { NamespaceResolutionService } from '../../namespace-resolution.service';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-crm-reports',
  imports: [
    CommonModule,
    MatButtonModule,
    MatExpansionModule,
    MatCardModule,
    MatIconModule,
    GalaxyBadgeModule,
    MatMenuModule,
    GalaxyLoadingSpinnerModule,
    TranslateModule,
  ],
  templateUrl: './reports.component.html',
  styleUrls: ['../association-panel-base-styles.component.scss'],
})
export class ReportsComponent implements OnInit {
  private readonly data = inject(COMPANY_PROFILE_CARD_DATA_TOKEN);
  private readonly snapshotService = inject(SnapshotService);
  private readonly namespaceResolutionService = inject(NamespaceResolutionService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);
  private readonly accountCreationService = inject(CompanyAccountService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly snapshotLookupId$$ = new ReplaySubject<string>(1);
  private readonly snapshotLookupId$ = this.snapshotLookupId$$.pipe(take(1));

  private readonly refreshingSnapshot$$ = new BehaviorSubject<boolean>(false);
  private readonly refreshingSnapshot$ = this.refreshingSnapshot$$.asObservable();

  private snapshotUrl$: Observable<SnapshotUrl>;

  snapshotName$: Observable<string>;
  snapshot$: Observable<CurrentSnapshot>;

  snapshotCardReady$: Observable<boolean>;
  reportCardsReady$: Observable<boolean>;
  creatingSnapshot$: Observable<boolean>;
  snapshotLoading$: Observable<boolean>;

  now = new Date();

  accountGroupId$ = this.accountCreationService.accountGroupId$;
  isAccountGroupLoading = toSignal(this.accountCreationService.createAccountGroupLoading$(this.data.company_id));

  ngOnInit(): void {
    this.setupAccountGroupIdFetching();
    this.setupSnapshotFetching();
    this.reportCardsReady$ = combineLatest([this.snapshotCardReady$]).pipe(map(() => true));
  }

  private setupAccountGroupIdFetching(): void {
    this.accountCreationService.setAccountGroupId(this.data.account_group_id ?? '');
    this.snapshotLookupId$$.next(this.data.account_group_id ?? '');
  }

  private setupSnapshotFetching(): void {
    this.accountGroupId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id) => {
      this.snapshotService.getCurrentSnapshotV2$(id);
    });
    this.snapshotLookupId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id) => {
      this.snapshotService.fetchSnapshotName(id);
    });

    const refresh$ = combineLatest([
      this.namespaceResolutionService.namespace$,
      this.accountGroupId$,
      this.refreshingSnapshot$,
    ]).pipe(
      filter(([partnerId, accountGroupId, refreshing]) => !!partnerId && !!accountGroupId && refreshing),
      map(
        ([partnerId, accountGroupId]) =>
          ({
            origin: 'company-profile',
            partnerId,
            accountGroupId,
          }) as CreateSnapshotRequest,
      ),
      switchMap((req) => this.snapshotService.refreshCompanySnapshot(req)),
    );

    refresh$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((refresh) => {
      if (refresh) {
        this.snapshotService.showRefreshSnapshotSuccessMessage();
      } else {
        this.snapshotService.showRefreshSnapshotErrorMessage();
      }
    });

    this.snapshot$ = this.accountGroupId$.pipe(switchMap((id) => this.snapshotService.getCurrentSnapshotUpdates$(id)));

    this.snapshotName$ = this.snapshotLookupId$.pipe(switchMap((id) => this.snapshotService.getSnapshotName$(id)));
    this.snapshotUrl$ = this.snapshot$.pipe(
      switchMap((snapshot) => this.snapshotService.getSnapshotUrl$(snapshot.snapshotID)),
      shareReplay(1),
    );

    const snapshotReady$ = this.accountGroupId$.pipe(
      switchMap((id) => this.snapshotService.currentSnapshotLoading$(id)),
      filter((loading) => loading === false),
    );

    this.snapshotLoading$ = this.accountGroupId$.pipe(
      switchMap((id) => this.snapshotService.currentSnapshotLoading$(id)),
    );

    this.snapshotCardReady$ = combineLatest([snapshotReady$, this.snapshotName$]).pipe(
      map(() => true),
      startWith(false),
    );

    const createSnapshotLoading$ = this.accountGroupId$.pipe(
      distinctUntilChanged(),
      switchMap((id) => this.snapshotService.createSnapshotLoading$(id)),
    );
    this.creatingSnapshot$ = combineLatest([
      this.accountCreationService.createAccountGroupLoading$(this.data.company_id),
      createSnapshotLoading$,
    ]).pipe(map(([accountGroup, snapshot]) => accountGroup || snapshot));
  }

  private openSnapshot(url: string) {
    window.open(url, '_blank', 'noopener');
  }

  async navToViewSnapshot(): Promise<void> {
    await firstValueFrom(this.snapshotUrl$).then(
      (url: SnapshotUrl) => this.openSnapshot(url.view),
      (_: Error) => {
        const err = this.translate.instant(
          'CRM.ASSOCIATION_PANEL.REPORTS.SNAPSHOT_REPORT_CARD.ERRORS.UNABLE_TO_GET_URL',
        );
        this.snackbarService.openErrorSnack(err);
      },
    );
  }

  async navToEditSnapshot(): Promise<void> {
    await firstValueFrom(this.snapshotUrl$).then(
      (url: SnapshotUrl) => this.openSnapshot(url.edit),
      (_: Error) => {
        const err = this.translate.instant(
          'CRM.ASSOCIATION_PANEL.REPORTS.SNAPSHOT_REPORT_CARD.ERRORS.UNABLE_TO_GET_URL',
        );
        this.snackbarService.openErrorSnack(err);
      },
    );
  }

  refreshSnapshot(): void {
    this.refreshingSnapshot$$.next(true);
  }

  createSnapshot(): void {
    const accountGroupId$ = this.accountCreationService.fetchAccountGroupId$(this.data.company_id);
    const work$ = combineLatest([this.namespaceResolutionService.namespace$, accountGroupId$]).pipe(
      map(([partnerId, accountGroupId]) => {
        if (accountGroupId) {
          return {
            origin: 'company-profile',
            partnerId,
            accountGroupId,
          } as CreateSnapshotRequest;
        }
        throw new Error('No account group ID available');
      }),
      switchMap((req) => this.snapshotService.createCompanySnapshot(req)),
    );
    work$.pipe(take(1)).subscribe({
      next: (snapshotId) => {
        if (snapshotId) {
          this.snapshotService.showCreateSnapshotSuccessMessage();
        }
      },
      error: () => this.snapshotService.showCreateSnapshotErrorMessage(),
    });
  }
}
